import { useMemo } from "react";
// import { Link2, Copy, Check, Edit, Eye } from "lucide-react";
import { FormSection } from "../types";
import { Template } from "../api/endpoints/fetchTemplates";
import { extractSectionsAndSubsections, getFormStats, Section } from "../data/helper";
import { useXFAFormTemplateDetails } from "../hooks/useXFAFormTemplateDetails";
import FormInfoHeader from "./FormInfoHeader";
import FormStructureSummary from "./FormStructureSummary";
import SectionsPreview, { SectionPreviewItem } from "./SectionsPreview";
import AssignToManager from "./AssignToManager";
import SelectMember from "./SelectMember";
import { useAuth } from "../contexts/AuthContext";

interface XFAFormInfoPanelProps {
  template: Template;
  onEdit?: () => void;
  onPublish?: () => void;
}

export function XFAFormInfoPanel({ template }: XFAFormInfoPanelProps) {
  // const [copied, setCopied] = useState(false);
  // const shareableUrl = `${window.location.origin}/form/${template.fileId}`;

  // const handleCopy = () => {
  //   navigator.clipboard.writeText(shareableUrl);
  //   setCopied(true);
  //   setTimeout(() => setCopied(false), 2000);
  // };

  // const handlePublish = () => {
  //   onPublish?.();
  // };

  const { data: templateDetailsResponse, isLoading, isError, error } = useXFAFormTemplateDetails({
    fileId: template.fileId,
    userEmail: template.userEmail
  });

  const templateDetails = templateDetailsResponse?.data;
  const templateTitle = (templateDetails?.fileTitle?.trim() ? templateDetails.fileTitle : undefined) ?? (template?.fileTitle?.trim() ? template.fileTitle : undefined) ?? template?.fileName ?? "Untitled Template";
  const templateHtml = templateDetails?.htmlContent ?? "";
  const usingHtml = !!(templateHtml && templateHtml.trim().length > 0);

  const extractedSections = useMemo<Section[] | FormSection[]>(() => {
    if (usingHtml && templateHtml) return extractSectionsAndSubsections(templateHtml);
    return [];
  }, [usingHtml, templateHtml]);

  const formStats = useMemo(() => {
    if (usingHtml) return getFormStats(extractedSections as Section[]);
    const sections = (extractedSections as FormSection[]) ?? [];
    const sectionCount = sections.length;
    const subsectionCount = sections.reduce((acc, s) => acc + (s.subsections ?? []).length, 0);
    const totalFields = sections.reduce((acc, s) => acc + (s.subsections ?? []).reduce((a, sub) => a + (sub.fields ?? []).length, 0), 0);
    return { sectionCount, subsectionCount, totalFields };
  }, [usingHtml, extractedSections]);

  const { sectionCount, subsectionCount, totalFields } = formStats;
  const estimatedTime = Math.max(1, Math.ceil(totalFields * 0.5));

  const templateDetailsErrorMessage = error && typeof error === "object" && "message" in error ? String((error as { message?: unknown }).message ?? "") : undefined;

  const structureIsLoading = isLoading;
  const structureErrorMessage = isError ? (templateDetailsErrorMessage || "") : undefined;

  const previewItems: SectionPreviewItem[] = useMemo(() => {
    const secs = (extractedSections as Section[]) ?? [];
    return secs.map((s) => ({ id: s.id, name: s.name, subsections: s.subsections.map((sub) => ({ id: sub.id, name: sub.name })) }));
  }, [extractedSections]);

  const { isAdmin } = useAuth();


  return (
    <div className="flex flex-col h-full">
      <div className="p-6 space-y-6 flex-1 overflow-y-auto">
        <FormInfoHeader
          title={templateTitle}
          published={template.published === "Published"}
          estimatedTime={estimatedTime}
          isLoadingEstimatedTime={isLoading}
          estimatedTimeErrorMessage={templateDetailsErrorMessage}
        />

        <div className="mt-8">
          {/* Only show if template has htmlId (required for assignment) */}
          {template.htmlId && (
            <>
              {/* Show AssignToManager if admin and published */}
              {isAdmin && template.isPublished && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-1 h-4 rounded-full" style={{ backgroundColor: "var(--color-primary)" }}></span>
                    Assign to Manager
                  </h4>
                  <AssignToManager htmlId={template.htmlId} />
                </div>
              )}
              {/* Show SelectMember if not admin */}
              {!isAdmin && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-1 h-4 rounded-full" style={{ backgroundColor: "var(--color-primary)" }}></span>
                    Assign to Member
                  </h4>
                  <SelectMember
                    htmlId={template.htmlId}
                    userId={template.memberId}
                    disabled={!!template.memberId}
                    onMemberAssigned={() => { }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div>
          <FormStructureSummary
            isLoading={structureIsLoading}
            errorMessage={structureErrorMessage}
            sectionCount={sectionCount}
            subsectionCount={subsectionCount}
            fieldCount={totalFields}
          />
        </div>

        <div>
          <SectionsPreview isLoading={structureIsLoading} errorMessage={structureErrorMessage} items={previewItems} />
        </div>

        {/* {template.published && (
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-1 h-4 rounded-full" style={{ backgroundColor: "var(--color-primary)" }}></div>
              Shareable Link
            </h4>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-blue-400/10 to-indigo-400/10 rounded-full -translate-y-6 translate-x-6"></div>
              <div className="flex items-center gap-2 mb-3 relative">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-sm">
                  <Link2 className="w-4 h-4 text-white" />
                </div>
                <p className="text-sm text-gray-700 font-medium">Share this link to collect responses</p>
              </div>
              <div className="flex gap-3 relative">
                <input type="text" value={shareableUrl} readOnly className="flex-1 px-4 py-2.5 text-sm bg-white/80 backdrop-blur-sm border border-blue-200/50 rounded-lg focus:outline-none shadow-sm" />
                <button
                  onClick={handleCopy}
                  className="px-4 py-2.5 text-sm rounded-lg transition-all duration-200 flex items-center gap-2 font-medium shadow-sm hover:shadow-md"
                  style={{ backgroundColor: copied ? "var(--color-secondary)" : "var(--color-primary)", color: "var(--color-on-primary)" }}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )} */}
      </div>

      {/* <div className="p-6 border-t border-gray-200 space-y-3 bg-gradient-to-r from-gray-50/50 to-blue-50/30">
        {onEdit && (
          <button
            onClick={onEdit}
            className="w-full text-sm py-3 px-4 border-2 rounded-xl hover:shadow-md transition-all duration-200 flex items-center justify-center gap-2 font-medium group"
            style={{ borderColor: "var(--color-primary)", color: "var(--color-primary)" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--color-primary)";
              e.currentTarget.style.color = "var(--color-on-primary)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "var(--color-primary)";
            }}
          >
            <Edit className="w-4 h-4" />
            Edit Form
          </button>
        )}

        {!template.published ? (
          <button
            onClick={handlePublish}
            className="w-full text-sm font-medium py-3 px-4 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
            style={{ backgroundColor: "var(--color-primary)", color: "var(--color-on-primary)" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "var(--color-primary)";
            }}
          >
            Publish Form
          </button>
        ) : (
          <button className="w-full text-sm py-3 px-4 border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:shadow-md transition-all duration-200 flex items-center justify-center gap-2 font-medium text-gray-700">
            <Eye className="w-4 h-4" />
            Preview as User
          </button>
        )}
      </div> */}
    </div>
  );
}

export default XFAFormInfoPanel;
